import { useSession } from 'next-auth/react';
import GridDragAndDrop from '../../../../../Misc/GridDragAndDrop/GridDragAndDrop';
import styles from './styles.module.scss';
import { useMemo, useState, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faBox, faCubesStacked, faTableList, faTag, faSave, faEdit } from '@fortawesome/free-solid-svg-icons';
import NumberFormat from 'react-number-format';
import ToggleSwitch from '../../../../../FunctionalComponents/ToggleSwitch';
import { isEqual } from 'lodash';

import errorMsg from '../../../../../Functions/errorMsg';
import successMsg from '../../../../../Functions/successMsg';

import axios from 'axios';
import { BounceLoader } from 'react-spinners';

import Resizer from 'react-image-file-resizer';

// Function to resize image and convert it to base64
const resizeFile = (file) =>
  new Promise((resolve) => {
    Resizer.imageFileResizer(
      file,
      1500,
      1500,
      'PNG',
      100,
      0,
      (uri) => {
        resolve(uri);
      },
      'base64'
    );
  });

export default function EditProduct({ productID, close }) {
  const { data: session, status: sessionStatus } = useSession();

  const [hasError, setHasError] = useState(false);
  const [loadingProduct, setLoadingProduct] = useState(false);
  const [productData, setProductData] = useState(null);

  const [pendingRequest, setPendingRequest] = useState(false);
  const [productName, setProductName] = useState('');
  const [productDescription, setProductDescription] = useState('');
  const [productPrice, setProductPrice] = useState('');
  const [productCategory, setProductCategory] = useState('');
  const [productQuantity, setProductQuantity] = useState('');
  const [productStatus, setProductStatus] = useState('Draft'); // Default to Draft
  const [uploads, setUploads] = useState([]);
  const [isEditEnabled, setIsEditEnabled] = useState(false);
  const [isSaveDraftEnabled, setIsSaveDraftEnabled] = useState(false);

  useEffect(() => {
    if (loadingProduct || productData || sessionStatus !== 'authenticated' || hasError) return;
  
    async function fetchProductsData() {
      setLoadingProduct(true);
      try {
        const getResult = await axios.post(`${window.location.origin}/api/vendor/manage/products/getProductData`, {
          productID: productID
        });
  
        if (getResult && getResult.status === 200) {
          const { name, description, price, category, stock, status, images } = getResult.data;
  
          // Sort images by position
          const sortedImages = images.sort((a, b) => a.position - b.position);
  
          setProductData(getResult.data);
          setProductName(name);
          setProductDescription(description);
          setProductPrice(price);
          setProductCategory(category);
          setProductQuantity(stock);
          setProductStatus(status);
          setUploads(sortedImages || []);
        }
      } catch (error) {
        errorMsg(error?.response?.data?.status || error.toString());
        setHasError(true)
      } finally {
        setLoadingProduct(false);
      }
    }
  
    if (session && sessionStatus === 'authenticated') {
      fetchProductsData();
    }
  }, [loadingProduct, productData, session, sessionStatus, productID]);

  // Form Validation and Edit Detection
  useEffect(() => {
    const isFormValid =
      productName.length >= 5 &&
      productName.length <= 42 &&
      productDescription.length >= 24 &&
      productDescription.length <= 1000 &&
      productPrice > 0 &&
      productCategory.length >= 4 &&
      productCategory.length <= 50 &&
      productQuantity > 0 &&
      uploads.length > 0;

    const isDataEdited =
      productName !== productData?.name ||
      productDescription !== productData?.description ||
      parseFloat(productPrice) !== parseFloat(productData?.price) ||
      productCategory !== productData?.category ||
      parseInt(productQuantity) !== parseInt(productData?.stock) ||
      productStatus !== productData?.status ||
      !isEqual(uploads, productData?.images || []);

    setIsEditEnabled(isFormValid && isDataEdited);
    setIsSaveDraftEnabled(isDataEdited && productStatus === 'Draft');
  }, [productName, productDescription, productPrice, productCategory, productQuantity, uploads, productStatus, productData]);

  // Handlers for Input Changes
  function handleProductNameChange(e) {
    let value = e.target.value;
    value = value.replace(/[^a-zA-Z0-9-:& ]/g, ''); // Allow specific characters
    value = value.replace(/\s{2,}/g, ' '); // Replace multiple spaces with a single space
    setProductName(value);
  }

  function handleProductDescriptionChange(e) {
    const formattedDescription = e.target.value.replace(/[^a-zA-Z0-9!@#$%&()/.,"':;[\]~+=-_ ]/g, '');
    setProductDescription(formattedDescription);
  }

  function handleCategoryChange(e) {
    let value = e.target.value.replace(/[^a-zA-Z0-9 ]/g, '');
    value = value.replace(/\b\w/g, (char) => char.toUpperCase());
    setProductCategory(value);
  }

  // Handler for Toggle Switch
  function handleStatusChange(newState) {
    setProductStatus(newState ? 'Active' : 'Inactive');
  }

  // Function to prepare images for upload
  async function prepareImagesForUpload(images) {
    return Promise.all(
      images.map(async (image, index) => {
        if (image.url.startsWith('blob:')) {
          const resizedImage = await resizeFile(image.imageFile);
          return { position: index + 1, url: resizedImage };
        }
        return { position: index + 1, url: image.url };
      })
    );
  }

  // Publish Product Function
  async function publishProduct() {
    if (pendingRequest) return;

    // Immediately set pending request to true to show spinner
    setPendingRequest(true);

    if (!isEditEnabled) {
      errorMsg('Please ensure all required fields are correctly filled and changes have been made.');
      setPendingRequest(false);
      return;
    }

    // Allow the component to re-render with the spinner
    await new Promise((resolve) => setTimeout(resolve, 0));

    // Change the status to 'Active' when publishing
    const updatedStatus = productStatus === 'Draft' ? 'Active' : productStatus;

    const preparedImages = await prepareImagesForUpload(uploads);

    const productPayload = {
      productName,
      productDescription,
      productPrice,
      productCategory,
      productQuantity,
      images: preparedImages,
      status: updatedStatus,
    };

    try {
      const result = await axios.post(`${window.location.origin}/api/vendor/manage/products/saveProduct`, {
        productID: productID,
        productData: productPayload
      });

      if (result && result.status === 200) {
        setProductStatus(updatedStatus);
        successMsg('Product changes are now saved.');
        close();
      }
    } catch (error) {
      errorMsg(error?.response?.data?.status || error.toString());
    } finally {
      setPendingRequest(false);
    }
  }

  // Save Draft Function
  async function saveDraftProduct() {
    if (pendingRequest) return;

    // Immediately set pending request to true to show spinner
    setPendingRequest(true);

    if (!isSaveDraftEnabled) {
      errorMsg('Please make changes or upload images to save a draft.');
      setPendingRequest(false);
      return;
    }

    // Allow the component to re-render with the spinner
    await new Promise((resolve) => setTimeout(resolve, 0));

    const preparedImages = await prepareImagesForUpload(uploads);

    const productPayload = {
      productName,
      productDescription,
      productPrice,
      productCategory,
      productQuantity,
      images: preparedImages,
      status: 'Draft'
    };

    try {
      const result = await axios.post(`${window.location.origin}/api/vendor/manage/products/saveProduct`, {
        productID: productID,
        productData: productPayload
      });

      if (result && result.status === 200) {
        successMsg('Product saved under drafts.');
        close();
      }
    } catch (error) {
      errorMsg(error?.response?.data?.status || error.toString());
    } finally {
      setPendingRequest(false);
    }
  }

  // Memoized Image Grid
  const memoizeImageGrid = useMemo(
    () => (
      <GridDragAndDrop
        initialImages={productData?.images || []}
        pendingRequest={pendingRequest}
        receiveUploadData={(newUploads) => setUploads(newUploads)}
      />
    ),
    [pendingRequest, productData?.images]
  );

  // Loading State
  function isLoading() {
    return loadingProduct || sessionStatus === 'loading' || !productData || pendingRequest;
  }

  if (isLoading()) {
    return (
      <section id={styles.loading}>
        <BounceLoader size={75} speedMultiplier={2} color="#154734" />
      </section>
    );
  }

  return (
    <section id={styles.editProductWrapper}>
      <div className={styles.detailsContainer}>
        <h1>
          Edit Product <FontAwesomeIcon icon={faBox} id={styles.icon} />
        </h1>

        <div className={styles.basicInfoCard}>
          <h1>Name</h1>
          <input type="text" minLength={5} maxLength={42} value={productName} onChange={handleProductNameChange} />
        </div>

        <div className={styles.basicInfoCard}>
          <h1>Description</h1>
          <textarea
            type="text"
            minLength={24}
            maxLength={1000}
            value={productDescription}
            onChange={handleProductDescriptionChange}
          />
        </div>

        <section className={styles.basicInfoContainer}>
          <div>
            <h1>
              Price <FontAwesomeIcon icon={faTag} id={styles.icon} />
            </h1>
            <NumberFormat
              onValueChange={(values) => setProductPrice(values.floatValue)}
              isAllowed={priceValueCap}
              placeholder="$14.99"
              decimalScale={2}
              thousandSeparator={true}
              prefix={'$'}
              value={productPrice}
            />
          </div>
          <div>
            <h1>
              Category <FontAwesomeIcon icon={faTableList} id={styles.icon} />
            </h1>
            <input
              type="text"
              placeholder="Product Category"
              onChange={handleCategoryChange}
              value={productCategory}
            />
          </div>
          <div>
            <h1>
              Stock <FontAwesomeIcon icon={faCubesStacked} id={styles.icon} />
            </h1>
            <NumberFormat
              onValueChange={(values) => setProductQuantity(values.floatValue)}
              isAllowed={quantityValueCap}
              placeholder="42"
              decimalSeparator={false}
              thousandSeparator={true}
              suffix=" / qty"
              value={productQuantity}
            />
          </div>
        </section>

        <div className={styles.actionBar}>
          <section id={styles.buttonGroup}>
            <button disabled={!isEditEnabled} onClick={publishProduct} className={styles.publishButton}>
              {productStatus === 'Draft' ? 'Publish' : 'Save'} <FontAwesomeIcon icon={faEdit} />
            </button>
            {productStatus === 'Draft' && (
              <button
                disabled={!isSaveDraftEnabled}
                onClick={saveDraftProduct}
                className={styles.draftButton}
              >
                Save <FontAwesomeIcon icon={faSave} />
              </button>
            )}
            <button onClick={close} className={styles.cancelButton}>
              Cancel
            </button>
          </section>

          {productStatus !== 'Draft' && (
            <section id={styles.activeProduct}>
              <h1>Product Active: </h1>
              <ToggleSwitch
                isDisabled={pendingRequest}
                state={productStatus === 'Active'}
                setState={handleStatusChange}
              />
            </section>
          )}
        </div>
      </div>

      <div className={styles.uploadContainer}>
        <section>{memoizeImageGrid}</section>
        <section>
          <h1>Product Images</h1>
          <h2>{uploads.length} / 12 Slots</h2>
        </section>
      </div>
    </section>
  );
}

/* Misc Functions */

const priceValueCap = (inputObj) => {
  const { value } = inputObj;
  return value <= 1000000000;
};

const quantityValueCap = (inputObj) => {
  const { value } = inputObj;
  return value <= 1000000;
};
